import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, Truck, CheckCircle, Clock, AlertCircle, User, 
  Mail, MapPin, ShoppingBag, ExternalLink, RefreshCw, Eye,
  ChevronDown, ChevronUp, Calendar, CreditCard, Hash
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Orders = () => {
  const { user, token, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');

  useEffect(() => {
    // Wait for auth loading to complete before checking user
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user, navigate, authLoading]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOrders();
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'shipped':
        return <Truck className="w-5 h-5 text-blue-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'confirmed':
        return <CheckCircle className="w-5 h-5 text-purple-600" />;
      case 'cancelled':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Package className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'shipped':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusMessage = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'Your order has been delivered!';
      case 'shipped':
        return 'Your order is on its way!';
      case 'processing':
        return 'Your order is being prepared.';
      case 'confirmed':
        return 'Your order has been confirmed.';
      case 'pending':
        return 'Your order is pending confirmation.';
      case 'cancelled':
        return 'This order has been cancelled.';
      default:
        return 'Order status unknown.';
    }
  };

  const getTrackingUrl = (carrier, trackingNumber) => {
    if (!carrier || !trackingNumber) return null;
    const c = carrier.toLowerCase();
    if (c.includes('usps')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    if (c.includes('ups')) return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    if (c.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    if (c.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingNumber}`;
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const statusSteps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];

  const getProgressWidth = (status) => {
    const index = statusSteps.indexOf(status?.toLowerCase());
    if (index === -1) return 0;
    return (index / (statusSteps.length - 1)) * 100;
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="loading-spinner">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="orders-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1
              className="text-3xl font-bold mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="orders-title"
            >
              My Account
            </h1>
            <p className="text-gray-600">Welcome back, {user?.first_name || 'Customer'}!</p>
          </div>
          <div className="flex items-center gap-3 mt-4 md:mt-0">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-all disabled:opacity-50"
              data-testid="refresh-orders-button"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={logout}
              className="px-4 py-2 text-sm text-red-600 hover:text-red-700 bg-red-50 border border-red-200 rounded-lg hover:shadow-sm transition-all"
              data-testid="logout-button"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'orders' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="orders-tab"
          >
            <ShoppingBag className="w-4 h-4 inline-block mr-2" />
            My Orders ({orders.length})
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'profile' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="profile-tab"
          >
            <User className="w-4 h-4 inline-block mr-2" />
            Profile
          </button>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <>
            {orders.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm" data-testid="no-orders">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
                  <Package className="w-10 h-10 text-gray-400" />
                </div>
                <h2 className="text-xl font-semibold mb-2">No orders yet</h2>
                <p className="text-gray-600 mb-6">When you place an order, it will appear here.</p>
                <Link
                  to="/shop"
                  className="inline-block bg-[#D4AF37] text-white py-3 px-8 rounded-full hover:bg-[#B5952F] transition-colors font-medium"
                  data-testid="start-shopping-button"
                >
                  Start Shopping
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order, index) => (
                  <div
                    key={order.id}
                    className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow"
                    data-testid={`order-${index}`}
                  >
                    {/* Order Header */}
                    <div 
                      className="p-5 cursor-pointer"
                      onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl ${getStatusColor(order.status).replace('text-', 'bg-').split(' ')[0]}`}>
                            {getStatusIcon(order.status)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg" data-testid={`order-number-${index}`}>
                                #{order.order_number}
                              </h3>
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`} data-testid={`order-status-${index}`}>
                                {order.status?.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              <Calendar className="w-3.5 h-3.5 inline-block mr-1" />
                              {formatDate(order.created_at)} • {order.items?.length || 0} item(s)
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-2xl font-bold" data-testid={`order-total-${index}`}>
                              ${order.total?.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">{getStatusMessage(order.status)}</p>
                          </div>
                          {expandedOrder === order.id ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Mini Progress Bar */}
                      {order.status !== 'cancelled' && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#B5952F] transition-all duration-500 rounded-full"
                              style={{ width: `${getProgressWidth(order.status)}%` }}
                            />
                          </div>
                          <div className="flex justify-between mt-2 text-xs text-gray-500">
                            <span>Ordered</span>
                            <span>Confirmed</span>
                            <span>Processing</span>
                            <span>Shipped</span>
                            <span>Delivered</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Expanded Details */}
                    {expandedOrder === order.id && (
                      <div className="border-t bg-gray-50 p-5">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Order Items */}
                          <div>
                            <h4 className="font-semibold mb-3 flex items-center gap-2 text-sm uppercase tracking-wide text-gray-600">
                              <Package className="w-4 h-4" />
                              Order Items
                            </h4>
                            <div className="bg-white rounded-lg border p-4 space-y-3">
                              {order.items?.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0" data-testid={`order-item-${index}-${idx}`}>
                                  <div>
                                    <p className="font-medium text-gray-900">{item.product_name}</p>
                                    <p className="text-sm text-gray-500">
                                      Qty: {item.quantity} {item.size && `• Size: ${item.size}`} {item.color && `• ${item.color}`}
                                    </p>
                                  </div>
                                  <span className="font-semibold">${item.subtotal?.toFixed(2)}</span>
                                </div>
                              ))}
                              <div className="pt-3 space-y-1 text-sm">
                                <div className="flex justify-between text-gray-600">
                                  <span>Subtotal</span>
                                  <span>${order.subtotal?.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                  <span>Shipping</span>
                                  <span>${order.shipping_cost?.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                  <span>Tax</span>
                                  <span>${order.tax?.toFixed(2)}</span>
                                </div>
                                {order.discount_amount > 0 && (
                                  <div className="flex justify-between text-green-600">
                                    <span>Discount</span>
                                    <span>-${order.discount_amount?.toFixed(2)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                                  <span>Total</span>
                                  <span>${order.total?.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Shipping & Tracking */}
                          <div className="space-y-4">
                            {/* Shipping Address */}
                            <div>
                              <h4 className="font-semibold mb-3 flex items-center gap-2 text-sm uppercase tracking-wide text-gray-600">
                                <MapPin className="w-4 h-4" />
                                Shipping Address
                              </h4>
                              <div className="bg-white rounded-lg border p-4 text-sm">
                                <p className="font-medium">{order.shipping_address?.first_name} {order.shipping_address?.last_name}</p>
                                <p className="text-gray-600">{order.shipping_address?.address}</p>
                                <p className="text-gray-600">
                                  {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.zip_code}
                                </p>
                                <p className="text-gray-500 mt-2">{order.shipping_address?.phone}</p>
                              </div>
                            </div>

                            {/* Tracking Info */}
                            {order.tracking_number && (
                              <div>
                                <h4 className="font-semibold mb-3 flex items-center gap-2 text-sm uppercase tracking-wide text-gray-600">
                                  <Truck className="w-4 h-4" />
                                  Tracking Information
                                </h4>
                                <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-sm text-blue-600 font-medium">{order.carrier || 'Carrier'}</p>
                                      <p className="font-mono font-bold text-lg">{order.tracking_number}</p>
                                    </div>
                                    {getTrackingUrl(order.carrier, order.tracking_number) && (
                                      <a
                                        href={getTrackingUrl(order.carrier, order.tracking_number)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-700 transition-colors"
                                        data-testid={`track-package-${index}`}
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                        Track
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Payment Info */}
                            <div>
                              <h4 className="font-semibold mb-3 flex items-center gap-2 text-sm uppercase tracking-wide text-gray-600">
                                <CreditCard className="w-4 h-4" />
                                Payment
                              </h4>
                              <div className="bg-white rounded-lg border p-4 text-sm">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Payment Status</span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    order.payment_status === 'paid' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {order.payment_status?.toUpperCase() || 'PENDING'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Need Help */}
                        <div className="mt-6 pt-4 border-t flex flex-wrap items-center justify-between gap-4">
                          <p className="text-sm text-gray-500">
                            Need help with this order? <Link to="/contact" className="text-[#D4AF37] hover:underline">Contact us</Link>
                          </p>
                          <Link
                            to={`/track-order`}
                            className="text-sm text-[#D4AF37] hover:text-[#B5952F] font-medium flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            Track on dedicated page
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="profile-section">
            <h2 className="text-xl font-semibold mb-6">Profile Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">First Name</label>
                  <p className="text-lg font-medium">{user?.first_name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Name</label>
                  <p className="text-lg font-medium">{user?.last_name || '-'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                  <p className="text-lg font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {user?.email || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Member Since</label>
                  <p className="text-lg font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {user?.created_at ? formatDate(user.created_at) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t">
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/track-order"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  <Package className="w-4 h-4" />
                  Track an Order
                </Link>
                <Link
                  to="/wishlist"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  <ShoppingBag className="w-4 h-4" />
                  My Wishlist
                </Link>
                <Link
                  to="/contact"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
